import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
  Card,
  CardContent,
  Avatar,
  Rating as MuiRating,
  Divider,
  Pagination,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { exploreApi } from '../services/api';
import { CafeVisit } from '../types/explore';
import toast from 'react-hot-toast';
import {
  Coffee,
  ArrowBack,
  Star,
  AccessTime,
  PendingActions,
  Search,
  Clear,
  History,
  Favorite,
  Share,
  LocalCafe,
  TrendingUp,
  RateReview
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import heroImage from '../assets/WFHCafe_WObg.png'; 
import StatsSection from '../components/Explore/StatsSection';
import { PreviouslyViewedSkeleton } from '../components/skeletons';

const PreviouslyViewedPage: React.FC = () => {
  const [visits, setVisits] = useState<CafeVisit[]>([]);
  const [filteredVisits, setFilteredVisits] = useState<CafeVisit[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState<'idle' | 'checking' | 'success' | 'error'>('idle');
  const [apiError, setApiError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'rated' | 'pending'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'rating' | 'name'>('recent');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Glass Effect (Applied to containers AND cards in dark mode)
  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';

  useEffect(() => {
    if (user) {
      checkApiConnection();
    }
  }, [user, page]);

  useEffect(() => {
    filterAndSortVisits();
  }, [visits, searchTerm, activeTab, sortBy]);

  const checkApiConnection = async () => {
    try {
      setApiStatus('checking');
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please login.');
      }
      
      const response = await exploreApi.getVisitHistory({
        page,
        limit: 12
      });
      
      if (response.success) {
        setApiStatus('success');
        setVisits(response.visits || []);
        const totalItems = response.visits.length;
        const totalPages = Math.ceil(totalItems / 12);
        setTotalPages(totalPages || 1);
      } else {
        throw new Error('Failed to fetch visits');
      }
      
    } catch (error: any) {
      console.error('❌ API Connection Error:', error);
      setApiStatus('error');
      setApiError(error.message);
      toast.error('Failed to load visits. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortVisits = () => {
    let result = [...visits];
    
    if (searchTerm) {
      result = result.filter(visit => 
        visit.cafeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (visit.feedback && visit.feedback.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (activeTab === 'rated') {
      result = result.filter(visit => visit.rating);
    } else if (activeTab === 'pending') {
      result = result.filter(visit => !visit.rating);
    }
    
    result.sort((a, b) => {
      const dateA = new Date(a.visitedAt);
      const dateB = new Date(b.visitedAt);
      
      switch (sortBy) {
        case 'recent':
          return dateB.getTime() - dateA.getTime();
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'name':
          return a.cafeName.localeCompare(b.cafeName);
        default:
          return 0;
      }
    });
    
    setFilteredVisits(result);
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleClearSearch = () => setSearchTerm('');

  const handleShareVisit = (visit: CafeVisit) => {
    const shareText = `I visited ${visit.cafeName} and ${visit.rating ? `rated it ${visit.rating}/5 stars!` : 'had a great time!'} #WFHCafe`;
    if (navigator.share) {
      navigator.share({ title: 'My Cafe Visit', text: shareText, url: window.location.href });
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Visit copied to clipboard!');
    }
  };

  const handleRateAgain = (cafeName: string) => {
    toast(`Would you like to visit ${cafeName} again?`, { duration: 3000, icon: '☕' });
  };

  // Logic to find the cafe name with the highest user rating from the list
  const getTopRatedCafeName = () => {
    const ratedVisits = visits.filter(v => v.rating && v.rating > 0);
    if (ratedVisits.length === 0) return '-';
    
    // Sort descending by rating
    const sortedByRating = [...ratedVisits].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return sortedByRating[0].cafeName;
  };

  const statsData = {
    totalCafes: visits.length,
    avgRating: visits.filter(v => v.rating).length > 0 
      ? parseFloat((visits.filter(v => v.rating).reduce((sum, v) => sum + (v.rating || 0), 0) / visits.filter(v => v.rating).length).toFixed(1))
      : 0,
    topRated: getTopRatedCafeName(), 
    mostReviewed: visits.filter(v => !v.rating).length // Using this field to show Pending count
  };

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: isDark ? '#000000' : '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, borderRadius: '32px', textAlign: 'center', ...glassEffect }}>
            <Coffee sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>Please Login</Typography>
            <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>You need to be logged in to view your previously visited cafes.</Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>Go to Login</Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: isDark ? '#000000' : '#FAFAFA' }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Back Button Redirects to Home */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')} 
          sx={{ mb: 3, color: goldColor, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } }}
        >
          Back to Home
        </Button>

        {/* Header with Image */}
        <Paper sx={{ p: 0, mb: 6, borderRadius: '32px', overflow: 'hidden', ...glassEffect, display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center' }}>
          <Box sx={{ p: { xs: 4, md: 6 }, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <History sx={{ fontSize: 40, color: goldColor, mr: 2 }} />
                <Typography variant="h3" sx={{ ...discordFont, background: isDark ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent', fontSize: { xs: '2rem', md: '3rem' } }}>
                    Your History
                </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', fontWeight: 400, maxWidth: 600 }}>
              Welcome back, {user.username}! Here you can manage your visits, rate cafes you've been to, and share your experiences.
            </Typography>
          </Box>
          <Box sx={{ width: { xs: '100%', md: '40%' }, height: { xs: 200, md: '100%' }, minHeight: { md: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 4 } }}>
             <img src={heroImage} alt="History Hero" style={{ width: '100%', height: '100%', objectFit: 'contain', maxHeight: 250 }} />
          </Box>
        </Paper>

        {/* Stats Grid - Using calculated topRated from local state */}
        <StatsSection stats={statsData} darkMode={isDark} />

        {/* Search and Filter */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: '20px', ...glassEffect }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <TextField
              fullWidth variant="outlined" placeholder="Search your visits..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)', '& fieldset': { borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)' }, '&:hover fieldset': { borderColor: goldColor }, '&.Mui-focused fieldset': { borderColor: goldColor } }, '& .MuiInputBase-input': { color: isDark ? '#FFFFFF' : deepGreen } }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><Search sx={{ color: goldColor, opacity: 0.7 }} /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><IconButton onClick={handleClearSearch} size="small"><Clear sx={{ fontSize: 18, color: goldColor }} /></IconButton></InputAdornment>)
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ '& .MuiTab-root': { color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', fontWeight: 600, textTransform: 'none', '&.Mui-selected': { color: goldColor } }, '& .MuiTabs-indicator': { backgroundColor: goldColor, borderRadius: '3px' } }}>
              <Tab label={`All (${visits.length})`} value="all" />
              <Tab label={`Rated`} value="rated" />
              <Tab label={`Pending`} value="pending" />
            </Tabs>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip icon={<AccessTime />} label="Recent" onClick={() => setSortBy('recent')} sx={{ bgcolor: sortBy === 'recent' ? (isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)') : 'transparent', color: sortBy === 'recent' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)'), border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}` }} />
              <Chip icon={<Star />} label="Rated" onClick={() => setSortBy('rating')} sx={{ bgcolor: sortBy === 'rating' ? (isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)') : 'transparent', color: sortBy === 'rating' ? goldColor : (isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)'), border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(196, 182, 111, 0.2)'}` }} />
            </Box>
          </Box>
        </Paper>

        {/* Visits Grid */}
        {loading ? (
          <PreviouslyViewedSkeleton />
        ) : filteredVisits.length === 0 ? (
          <Paper sx={{ p: 8, borderRadius: '24px', ...glassEffect, textAlign: 'center' }}>
            <Coffee sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
            <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>{searchTerm ? 'No Matching Visits' : 'No Visits Yet'}</Typography>
            <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4, maxWidth: 400, mx: 'auto' }}>{searchTerm ? `No visits found matching "${searchTerm}".` : "You haven't visited any cafes yet. Start exploring!"}</Typography>
            <Button variant="contained" onClick={() => searchTerm ? setSearchTerm('') : navigate('/explore')} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>{searchTerm ? 'Clear Search' : 'Explore Cafes'}</Button>
          </Paper>
        ) : (
          <>
            {/* Compact Grid with smaller cards */}
            <Grid container spacing={2.5} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {filteredVisits.map((visit) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={visit.id} sx={{ 
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                        width: '100%',
                        maxWidth: 320,
                        minWidth: { xs: 320, sm: 340, md: 350 },
                        height: '100%',
                        minHeight: 340,
                        borderRadius: '20px',
                        background: isDark 
                          ? glassEffect.background
                          : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,240,240,0.95) 100%)',
                        border: glassEffect.border,
                        boxShadow: glassEffect.boxShadow,
                        backdropFilter: 'blur(20px)',
                        overflow: 'hidden',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: isDark 
                              ? '0 20px 60px rgba(0,0,0,0.9)' 
                              : '0 20px 40px rgba(5, 24, 20, 0.15)',
                        }
                    }}
                  >
                    {/* Cafe Photo Header - Reduced height */}
                    <Box sx={{ 
                      position: 'relative', 
                      height: 140,
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {visit.cafePhotos && visit.cafePhotos.length > 0 ? (
                        <Box
                          component="img"
                          src={visit.cafePhotos[0]}
                          alt={visit.cafeName}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            background: lokiGradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <LocalCafe sx={{ fontSize: 48, color: goldColor, opacity: 0.7 }} />
                        </Box>
                      )}
                      
                      {/* Overlay with cafe name and rating - Compact */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                          p: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-end'
                        }}
                      >
                        <Typography 
                          variant="subtitle1"
                          sx={{ 
                            ...discordFont, 
                            color: '#fff', 
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            maxWidth: '70%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {visit.cafeName}
                        </Typography>
                        
                        {visit.rating && (
                          <Box
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: '#fff',
                              borderRadius: '8px',
                              px: 1,
                              py: 0.25,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              backdropFilter: 'blur(4px)',
                              border: `1px solid ${goldColor}`,
                              flexShrink: 0
                            }}
                          >
                            <Star sx={{ fontSize: 12, color: goldColor }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                              {visit.rating.toFixed(1)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Photo count badge - Smaller */}
                      {visit.cafePhotos && visit.cafePhotos.length > 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: '#fff',
                            borderRadius: '8px',
                            px: 1,
                            py: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            backdropFilter: 'blur(4px)',
                            border: `1px solid ${goldColor}`,
                            flexShrink: 0
                          }}
                        >
                          <LocalCafe sx={{ fontSize: 12, color: goldColor }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            {visit.cafePhotos.length}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <CardContent sx={{ 
                      p: 2,
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      '&:last-child': { pb: 2 }
                    }}>
                      {/* Visit info and time - Compact */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        mb: 1.5,
                        flexWrap: 'wrap',
                        gap: 0.5
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime sx={{ fontSize: 14, color: goldColor }} />
                          <Typography variant="body2" sx={{ 
                            color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)',
                            fontSize: '0.8rem'
                          }}>
                            {getTimeAgo(visit.visitedAt)}
                          </Typography>
                        </Box>
                        
                        {!visit.rating && (
                          <Chip
                            icon={<PendingActions sx={{ fontSize: 12 }} />}
                            label="Pending"
                            size="small"
                            sx={{
                              background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                              color: goldColor,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                        )}
                      </Box>

                      {/* Photo thumbnails - Smaller */}
                      {visit.cafePhotos && visit.cafePhotos.length > 1 && (
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5,
                          mb: 1.5,
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          {visit.cafePhotos.slice(0, 4).map((photo, index) => (
                            <Box
                              key={index}
                              component="img"
                              src={photo}
                              alt={`${visit.cafeName} photo ${index + 1}`}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '4px',
                                objectFit: 'cover',
                                border: `1px solid ${isDark ? 'rgba(196, 182, 111, 0.2)' : 'rgba(196, 182, 111, 0.1)'}`,
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  borderColor: goldColor
                                }
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ))}
                          {visit.cafePhotos.length > 4 && (
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '4px',
                                background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px dashed ${goldColor}`,
                                cursor: 'pointer'
                              }}
                            >
                              <Typography variant="caption" sx={{ color: goldColor, fontWeight: 700, fontSize: '0.65rem' }}>
                                +{visit.cafePhotos.length - 4}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Rating and Feedback Section - Compact */}
                      {visit.rating ? (
                        <Box sx={{ mb: 1.5, flexGrow: 1 }}>
                          {visit.feedback && (
                            <Typography variant="body2" sx={{ 
                              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)',
                              fontStyle: 'italic', 
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontSize: '0.8rem',
                              minHeight: 40
                            }}>
                              "{visit.feedback}"
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ 
                          p: 1.5,
                          borderRadius: '8px',
                          textAlign: 'center',
                          mb: 1.5,
                          background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)',
                          flexGrow: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 50
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
                            fontStyle: 'italic',
                            fontSize: '0.8rem'
                          }}>
                            No review added
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ 
                        my: 1.5,
                        borderColor: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(5, 24, 20, 0.1)' 
                      }} />

                      {/* Action Buttons - Compact */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        mt: 'auto',
                        flexWrap: 'wrap',
                        gap: 0.5
                      }}>
                        <Button
                          startIcon={<Favorite sx={{ fontSize: 16 }} />}
                          onClick={() => handleRateAgain(visit.cafeName)}
                          size="small"
                          sx={{ 
                            color: goldColor,
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            px: 1.5,
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)'
                            }
                          }}
                        >
                          Visit Again
                        </Button>
                        <Button
                          startIcon={<Share sx={{ fontSize: 16 }} />}
                          onClick={() => handleShareVisit(visit)}
                          size="small"
                          sx={{ 
                            color: goldColor,
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            px: 1.5,
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)'
                            }
                          }}
                        >
                          Share
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                      '&.Mui-selected': {
                        background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814',
                        color: isDark ? '#051814' : '#FFFFFF',
                        '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' }
                      },
                      '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PreviouslyViewedPage;